import React, {ChangeEvent, Dispatch, useState} from 'react';
import ReactSelect from 'react-select/async';

import {Flow, FlowFetchResponse} from '../_services/Flow';
import {Etl} from '../_services/Etl';
import {Choice} from '../types';
import {ActivityButton} from './ActivityButton';
import {connect} from 'react-redux';
import {setLayoutEntryGutterComments} from '../_actions/layout';
import {LayoutEntryGutterCommentsState} from '../_reducers/layout';
import {getLayoutEntryGutterComments} from '../_selectors/layout';

const debounce = require('debounce-promise');

type EntryCommentsProps = {
    entryId: string;
    onSave: () => void;
    layout: LayoutEntryGutterCommentsState;
    dispatchLayoutUpdate: (layout: LayoutEntryGutterCommentsState) => void;
};

type EntryCommentsFormState = {
    comments: string;
    additionalRecipients: Choice[];
}

const initialFormState: EntryCommentsFormState = {
    comments: '',
    additionalRecipients: []
};

const EntryComments: React.FunctionComponent<EntryCommentsProps> = ({
                                                                        entryId,
                                                                        onSave,
                                                                        layout,
                                                                        dispatchLayoutUpdate
                                                                    }): JSX.Element => {
    const [commentsShowMoreOptions, setCommentsShowMoreOptions] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isWorking, setIsWorking] = useState<boolean>(false);
    const [formState, setFormState] = useState<EntryCommentsFormState>(initialFormState);

    // create a "minimized" view - this will be global state setting
    console.log('component layout', layout);

    const handleCommentSave = () => {
        setErrorMessage('');
        setIsWorking(true);

        const cc = formState.additionalRecipients.map((choice: Choice) => choice.value);

        Flow.getInstance().saveComments(entryId, formState.comments, cc)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    onSave && onSave();
                    setFormState(initialFormState);
                } else {
                    setErrorMessage(response.json.message || 'Error saving comments');
                }
                setIsWorking(false);
            })
            .catch((error: any) => {
                setErrorMessage(error || 'Error saving comments');
                setIsWorking(false);
            });
    };

    const handleEmailLookup = debounce(
        Etl.getInstance().employeeAutocompleter,
        250
    );

    return (
        <div className="container postbox" id="comments-container">
            <h3
                className="hndle d-flex justify-content-between align-items-center"
                style={{cursor: 'default'}}
            >
                <span>Comments</span>
                <small>
                    <i
                        className={[
                            'far',
                            layout.display === 'default' && 'fa-minus-square',
                            layout.display !== 'default' && 'fa-plus-square'
                        ].filter(Boolean).join(' ')}
                        style={{cursor: 'pointer'}}
                        onClick={() => {
                            dispatchLayoutUpdate({
                                ...layout,
                                display: layout.display === 'default' ? 'minimized' : 'default'
                            })
                        }}
                    />
                </small>
            </h3>
            {layout.display === 'default' &&
                <>
                    <p>Enter your comments</p>
                    <div className="form-group">
                <textarea className="form-control"
                          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                              setFormState({...formState, comments: e.target.value});
                          }}
                          value={formState.comments}
                          rows={5}
                />
                    </div>

                    {!commentsShowMoreOptions &&
                        <p><a href="#"
                              onClick={(e) => {
                                  e.preventDefault();
                                  setCommentsShowMoreOptions(true);
                              }}
                        >More Options</a></p>}

                    {commentsShowMoreOptions &&
                        <div>
                            <p><a href="#"
                                  onClick={(e) => {
                                      e.preventDefault();
                                      setCommentsShowMoreOptions(false);
                                  }}
                            >Less Options</a></p>

                            <p>An email notification for new comments is sent to the requester and current assignees. Include additional
                                recipients by entering their email addresses below.</p>

                            <div className="form-group">
                                <ReactSelect
                                    isMulti={true}
                                    value={formState.additionalRecipients}
                                    onChange={(option: any) => {
                                        setFormState({...formState, additionalRecipients: option});
                                    }}
                                    placeholder="Enter an email address"
                                    loadOptions={handleEmailLookup}
                                />
                            </div>
                        </div>
                    }

                    <div className="form-group d-flex justify-content-end">
                        <ActivityButton type="button"
                                        className="btn btn-primary"
                                        disabled={!formState.comments}
                                        isActive={isWorking}
                                        onClick={handleCommentSave}
                        >Submit
                        </ActivityButton>
                    </div>

                    {errorMessage && <p className="mt-2 text-right text-danger">{errorMessage}</p>}
                </>
            }
        </div>
    );
}

const mapStateToProps = (state: any) => ({
    layout: getLayoutEntryGutterComments(state)
});
const mapDispatchToProps = (dispatch: Dispatch<any>) => {
    return {
        dispatchLayoutUpdate: (layout: LayoutEntryGutterCommentsState) => {
            dispatch(setLayoutEntryGutterComments(layout));
        }
    };
};

const ConnectedEntryComments = connect(mapStateToProps, mapDispatchToProps)(EntryComments);
export {ConnectedEntryComments as EntryComments};